/**
 * Global warming drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawing_topics`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const globalWarmingDrawingsPack: ToilePack = {
    slug: 'global-warming-drawings',
    category: 'coloring',
    name: 'Global warming drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'melting', label: 'Melting earth', subject: 'the earth melting like an ice cream on a cone under a hot sun'},
        {key: 'bear', label: 'Polar bear', subject: 'a polar bear on a small shrinking piece of ice in open sea'},
        {key: 'thermometer', label: 'Hot sun', subject: 'a thermometer rising into a burning sun over a dry cracked land'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil sketch', style: 'a soft graphite pencil sketch with clear outlines and light hatching for shade', palette: 'Gray pencil on white paper, no color.', framing: 'Drawn on a plain white paper sheet, centered with a white margin.'},
        {key: 'crayon', label: 'Colour pencil', style: 'a colour pencil and crayon drawing, simple outlines filled with bright even strokes of colour', palette: 'Bright crayon colours on white paper.', framing: 'Drawn on a plain white drawing-book page, centered with a white margin.'},
        {key: 'outline', label: 'Outline to colour', style: 'a bold black outline drawing with flat marker colour filling each shape', palette: 'Black outlines with flat bright colour fills on white paper.', framing: 'Flat 2D drawing isolated on a pure white background with a white margin, clean closed black line work.'},
    ],
    directions: ['a dry field', 'melting glacier', 'a city'],
    directionHint: 'try: a dry field',
    seo: {
        title: 'Global Warming Drawings: Melting Earth, Polar Bear, Sun',
        metaDescription: 'Nine global warming drawings: a melting earth, a polar bear on shrinking ice and a burning sun, in pencil, colour and outline.',
        h1: 'Global warming drawings: melting earth, polar bear, thermometer sun',
        tagline: 'Three scenes, each in pencil sketch, colour pencil and outline to colour.',
        intro: 'Global warming drawings for a competition, a climate poster or a chart. The set shows the earth melting like an ice cream on a cone under a hot sun, a polar bear on ice in open sea, and a thermometer rising into a burning sun. Each comes in three styles: pencil, colour pencil and outline to colour. Type a change in the direction field, such as melting glacier, a dry field or a city.',
        useCases: [
            'a climate change poster for a competition',
            'a chart on global warming for a class',
            'a school project on the environment',
            'a classroom display on climate',
        ],
        faq: [
            {q: 'Can I print these for a school competition?', a: 'Yes. Each picture is a plain drawing on a white background, so it prints on ordinary paper.'},
            {q: 'What is the difference between the three styles?', a: 'Pencil sketch is grey shading with no colour. Colour pencil is bright crayon colour. Outline to colour is a bold black line drawing with flat colour inside each shape, easy to fill in.'},
            {q: 'What does the thermometer sun show?', a: 'It shows temperature rising as the sun heats dry land, a simple way to show warming.'},
            {q: 'Can I ask for a different picture?', a: 'Yes. Type the change in the direction field. It is a request, so the result may differ from the nine shown.'},
        ],
    },
}
